/** @format */

import { apiSlice } from "../apiSlice";
import { Text } from "../../features/resources/resourcesSlice";
import { ResourceReference } from "../../types";
import { store } from "../store";

export interface Subject {
  id: string;
  title: Text;
}

export interface SubjectResponse {
  subjects: Subject[];
}

export interface ResourcesFromSubjectResponse {
  resourceRefs: ResourceReference[];
  messages: any[];
}

/** API Endpoints related to Subject(s) */
export const subjectApiSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    /** 
    * getSubjects query (GET)
    * @returns {SubjectResponse} all available subjects.
    */
    getSubjects: build.query<SubjectResponse, void>({
      query: () => `subjects/get`,
    }),

    /** 
    * getSubjectsSingleLang query (GET)
    * @param {string} locale - Locale of subject either `en` or `cy`
    * @returns {Subject[]} all available subjects in a single language.
    */
    getSubjectsSingleLang: build.query<Subject[], string>({
      query: (locale) => `subjects/get/${locale}`,
    }),

    /** 
    * getResourcesFromSubject query (GET)
    * @param {string} code - The subject's id
    * @param {string} locale - Locale of subject either `en` or `cy`
    * @returns {ResourceReference[]} all available resources in a subject.
    */
    getResourcesFromSubject: build.query<
      ResourceReference[],
      { code: string; locale: string }
    >({
      query: ({ code, locale }) => {
        // If logged in, send request to auth endpoint
        if (store.getState().auth.userToken?.token) {
          return `subjects/resources/auth/${code}/${locale}`;
        }
        return `subjects/resources/${code}/${locale}`;
      },
      transformResponse: (response: { resourceRefs: ResourceReference[] }) =>
        response.resourceRefs,
      providesTags: (result) => 
        result
          ? [
              ...result.map(({ UUID }) => ({ type: 'ResourceRef' as const, id: UUID })),
              { type: 'ResourceRef', id: 'LIST' },
            ]
          : [{ type: 'ResourceRef', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetSubjectsSingleLangQuery,
  useGetSubjectsQuery,
  useGetResourcesFromSubjectQuery,
} = subjectApiSlice;

